import React, { createContext, useContext, useState } from 'react';
import { NumberArray } from '@wonderlandengine/api';
import { CloudData } from '../data/cloud-data.js';
import { Utilities } from '../utilities/utilities.js';
import { Configuration, PaintTypes, PaintColourWaysOptions, BespokePaintColours } from '../data/configuration.js';
import { PaintConfigurator } from '../configurator/paint-configurator.js';
import { InteriorConfigurator } from '../configurator/interior-configurator.js';
import { ExteriorConfigurator } from '../configurator/exterior-configurator.js';
import { VehicleView } from './vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from './view-content.js';
import SelectedSectionItem from './ui/selected-section-item.tsx';
import Modal, { ModalContent } from './ui/modal.tsx'
import ViewContentRows from './ui/view-content-rows.tsx';
import TabStrip, { TabDefinition } from './ui/tab-strip.tsx';
import BespokeColourPicker, { DEFAULT_BESPOKE_COLOUR, rgbToHex } from './ui/bespoke-colour-picker.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface PaintworkViewProps {
	paintConfigurator: PaintConfigurator;
	interiorConfigurator: InteriorConfigurator;
	exteriorConfigurator: ExteriorConfigurator;
	onBackButtonClicked: () => void;
	onNextButtonClicked: () => void;
}

// the part of the vehicle a colour is being chosen for. the wings can be painted a second colour, which is what makes the
// configuration two-tone, so each part is configured on its own tab.
enum PaintParts {
	Body = 'BODY',
	Wings = 'WINGS'
}

interface PaintViewProps {
	subHeading: string;
	paintType: PaintTypes;
	paintPart: PaintParts;
	itemId: string;
	itemName: string;
	sectionPrice: number;
	modalContent: ModalContent[];
	isClosed: boolean;
	bespokeColour: NumberArray | null;
	isBespokeSelected: boolean;
	onPanelOpenClose: (isOpen: boolean) => void;
	onItemClicked: (id: string) => void;
	onBespokeColourChanged: (rgba: NumberArray, paintType: PaintTypes) => void;
}

interface BespokePaintOptionProps {
	colour: NumberArray | null;
	isSelected: boolean;
	isPanelClosed: boolean;
	onColourChanged: (rgba: NumberArray) => void;
}

interface PaintItemsProps {
	paintType: PaintTypes;
}

interface PaintViewContextType {
	selectedItemId: string;
	handleItemClicked: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

// the back-office option id a bespoke colour is recorded as, per paint type. a paint type absent from this map does not offer the
// bespoke 'select your own' option at all - solitallic is fixed-colour only - so this one map decides both which panels carry the
// option and what selecting it records.
const BESPOKE_PAINT_OPTION_IDS: Partial<Record<PaintTypes, string>> = {
	[PaintTypes.Solid]: BespokePaintColours.Solid,
	[PaintTypes.Metallic]: BespokePaintColours.Metallic
};

// the icon the bespoke swatch shows until a colour has been chosen, so that the option reads as an invitation to choose one rather
// than as a colour already picked out for the customer. as with every configuration item image, the path is relative to the deploy
// root - 'static/' is dropped at runtime.
const SPECIAL_COLOUR_IMAGE_FILE_PATH = 'assets/images/configuration-items/paintwork/special-colour.webp';

// the information shown for a bespoke colour, both behind the panel's info icon while one is selected and on the customer's first
// selection of one. it replaces the back-office description of the bespoke option ids rather than adding to it.
const SPECIAL_PAINT_MODAL_CONTENT: ModalContent[] = [{
	title: 'SPECIAL PAINT INFORMATION',
	body: "All colours, textures and finishes represented in the Morgan Car Configurator will vary in appearance depending upon the device screen, or printer that is being used to view them. Therefore, exact finishes should be used for guidance only and checked with your Morgan Dealer. Morgan Motor Company are not able to replicate colours generated from the 'Special' colour functions on the Morgan Car Configurator. Suitable paint codes are required to produce special paint colours."
}];

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function PaintworkView(props: PaintworkViewProps) {

	// initial values.
	const initialBodyColourItemId = Configuration.bodyPaintColour;
	const initialBodyColourItemName = () => getItemName(initialBodyColourItemId);
	const initialWingColourItemId = Configuration.wingPaintColour;
	const initialWingColourItemName = () => getItemName(initialWingColourItemId);

	// the colours are presented as an accordion of one panel per paint type, of which the panel holding the body's own colour is the one open on arrival.
	const initialOpenPaintType = () => CloudData.getPaintTypeForId(initialBodyColourItemId);

	// states.
	const [selectedBodyColourItemId, setSelectedBodyColourItemId] = useState(initialBodyColourItemId);
	const [selectedBodyColourItemName, setSelectedBodyColourItemName] = useState(initialBodyColourItemName);
	const [selectedWingColourItemId, setSelectedWingColourItemId] = useState(initialWingColourItemId);
	const [selectedWingColourItemName, setSelectedWingColourItemName] = useState(initialWingColourItemName);
	const [selectedPaintPart, setSelectedPaintPart] = useState<PaintParts>(PaintParts.Body);

	// the one paint type panel currently open, or null when the customer has closed them all. holding the accordion as a single
	// value rather than as a flag per panel is what keeps the tab switch below to one line.
	const [openPaintType, setOpenPaintType] = useState<PaintTypes | null>(initialOpenPaintType);

	// a bespoke colour outlives this view, which is unmounted on every change of step, so both halves of it are read back from the
	// configuration rather than restarting from the picker's defaults: the paint type it was applied with is the one its option id
	// belongs to. the colour stays null until the customer has chosen one, which is the state that shows the icon rather than a
	// colour they were never offered. each painted region keeps its own pair, as the whole point of two-tone is two different colours.
	const [bespokeBodyColour, setBespokeBodyColour] = useState<NumberArray | null>(Configuration.bespokeBodyPaintColour);
	const [bespokeWingColour, setBespokeWingColour] = useState<NumberArray | null>(Configuration.bespokeWingPaintColour);
	const [bespokeBodyPaintType, setBespokeBodyPaintType] = useState<PaintTypes | null>(getBespokePaintTypeForId(initialBodyColourItemId));
	const [bespokeWingPaintType, setBespokeWingPaintType] = useState<PaintTypes | null>(getBespokePaintTypeForId(initialWingColourItemId));

	// the tab being configured decides which colour every panel shows as selected, what a swatch click changes, and what the
	// selected name, price and info modal describe - the panels themselves are the same on both tabs.
	const isWingsPartSelected = selectedPaintPart === PaintParts.Wings;
	const selectedItemId = isWingsPartSelected ? selectedWingColourItemId : selectedBodyColourItemId;
	const selectedItemName = isWingsPartSelected ? selectedWingColourItemName : selectedBodyColourItemName;
	const selectedBespokeColour = isWingsPartSelected ? bespokeWingColour : bespokeBodyColour;
	const selectedBespokePaintType = isWingsPartSelected ? bespokeWingPaintType : bespokeBodyPaintType;

	// the wings are only charged for when they differ from the body, as that is what makes the configuration two-tone.
	const selectedSectionPrice = isWingsPartSelected
		? (Configuration.isTwoTonePaint() ? CloudData.getPrice(selectedWingColourItemId, CloudData.paintworkColourOptions, { ref: false }) : 0)
		: CloudData.getPrice(selectedBodyColourItemId, CloudData.paintworkColourOptions, { ref: false });

	// the info modal describes the selected colour on the body tab, and the two-tone option itself on the wings tab.
	const selectedModalContent = isWingsPartSelected
		? CloudData.getModalContent(PaintColourWaysOptions.TwoTone, CloudData.paintworkColourWaysOptions)
		: getModalContent(selectedBodyColourItemId);

	// the tabs, each carrying a swatch of the colour that part is currently painted, so both choices can be seen from either tab.
	const paintPartTabs: TabDefinition[] = [
		{ id: PaintParts.Body, name: 'BODY', imageFilePath: getSwatchFilePathForColour(selectedBodyColourItemId, bespokeBodyColour) },
		{ id: PaintParts.Wings, name: 'WINGS', imageFilePath: getSwatchFilePathForColour(selectedWingColourItemId, bespokeWingColour) }
	];

	// handlers.

	const handlePaintPartClicked = (id: string) => {

		// move to the clicked tab.
		const paintPart = id as PaintParts;
		setSelectedPaintPart(paintPart);

		// and open the panel holding that part's colour, so its current selection is in view on arrival.
		const paintPartColourItemId = paintPart === PaintParts.Wings ? selectedWingColourItemId : selectedBodyColourItemId;
		setOpenPaintType(CloudData.getPaintTypeForId(paintPartColourItemId));
	};

	const handlePanelOpenClose = (paintTypePanel: PaintTypes, isOpen: boolean) => {

		// only one panel is open at a time, so opening one closes the others, and closing one leaves none open.
		setOpenPaintType(isOpen ? paintTypePanel : null);
	};

	const handleWingColourItemClicked = (id: string) => {

		// if the clicked wing colour is the same as the current wing colour, reset it to match the body colour.
		if (id === Configuration.wingPaintColour) {
			id = Configuration.bodyPaintColour;

			// going back to a bespoke body colour means taking its colour as well as its id, as the id alone does not say which
			// colour it stands for - the two regions record the same bespoke ids. going back to a standard one leaves the wings'
			// own remembered colour alone, so that re-opening their picker still returns to it.
			if (Configuration.isBespokePaintColour(id)) {
				setBespokeWingColour(Configuration.bespokeBodyPaintColour);
				Configuration.bespokeWingPaintColour = Configuration.bespokeBodyPaintColour;
			}
		}

		// update the selected item and item name.
		setSelectedWingColourItemId(id);
		setSelectedWingColourItemName(getItemName(id));

		// the wings now wear whichever paint type that colour belongs to, or none if it is a standard colour. the colour itself is
		// deliberately kept, so that re-opening the picker returns to it rather than to the default.
		setBespokeWingPaintType(getBespokePaintTypeForId(id));

		// update the configuration.
		Configuration.wingPaintColour = id;
		Configuration.updateCosts();

		// select the wing paint colour on the car. a bespoke id has no paint description, so the colour is applied from the
		// configuration instead.
		if (Configuration.isBespokePaintColour(id) && Configuration.bespokeWingPaintColour) {
			props.paintConfigurator.selectBespokeWingPaint(Configuration.bespokeWingPaintColour, CloudData.getPaintTypeForId(id));
		}
		else {
			props.paintConfigurator.selectWingPaint(id);
		}

		// the front half of the splitter wears the wing colour.
		props.exteriorConfigurator.selectSplitterWingPaint(id);
	}

	const handleBodyColourItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedBodyColourItemId(id);
		setSelectedBodyColourItemName(getItemName(id));

		// a standard colour is now selected, so the bespoke colour is no longer the applied one. the colour itself is deliberately
		// kept, so that re-opening the picker returns to it rather than to the default.
		setBespokeBodyPaintType(null);

		// note whether the wings have been following the body before its colour changes underneath them.
		const wereWingsFollowingBody = Configuration.isWingPaintFollowingBodyPaint();

		// update the configuration.
		Configuration.bodyPaintColour = id;
		Configuration.updateCosts();

		// select the body paint colour on the car.
		props.paintConfigurator.selectBodyPaint(id);
		props.exteriorConfigurator.selectSplitterBodyPaint(id);
		props.interiorConfigurator.selectDashboardOption(Configuration.dashboardOption);

		// match the wing paint colour on the car with the newly selected body colour if it was matching before.
		if (wereWingsFollowingBody) {
			handleWingColourItemClicked(id);
		}

		// update the last colour.
		Configuration.lastBodyPaintColour = id;
	};

	const handleBespokeColourChanged = (rgba: NumberArray, paintType: PaintTypes) => {

		// the two regions record the same bespoke option ids, so it is the colour, not the id, that can turn a matching wing into a
		// two-tone one - and the two-tone option carries a price of its own. both states are therefore noted before anything changes,
		// and the re-pricing below is guarded on either of them moving rather than on the option id alone.
		const wasTwoTonePaint = Configuration.isTwoTonePaint();
		const wereWingsFollowingBody = Configuration.isWingPaintFollowingBodyPaint();

		// the option id for this paint type, which is what carries the description and the price.
		const bespokeOptionId = BESPOKE_PAINT_OPTION_IDS[paintType];
		if (bespokeOptionId === undefined) {
			return;
		}

		// the wings take the colour whenever they are the region being painted, and also when they are following the body - in which
		// case they take the body's, so that the two stay one paint rather than drifting into a two-tone the customer did not ask for.
		const areWingsTakingColour = isWingsPartSelected || wereWingsFollowingBody;

		// whether this is the first move of a new selection, as opposed to another move of one already made.
		const hasOptionIdChanged = isWingsPartSelected
			? Configuration.wingPaintColour !== bespokeOptionId
			: Configuration.bodyPaintColour !== bespokeOptionId;

		// the same for the wings alone, which may be taking the colour without being the region painted - when following the body.
		const hasWingOptionIdChanged = Configuration.wingPaintColour !== bespokeOptionId;

		if (isWingsPartSelected) {

			// remember the colour and the paint type it was applied with, so that the swatch shows it and only that panel shows as selected.
			setBespokeWingColour(rgba);
			setBespokeWingPaintType(paintType);
			Configuration.bespokeWingPaintColour = rgba;

			// record the option id against the wings.
			setSelectedWingColourItemId(bespokeOptionId);
			setSelectedWingColourItemName(getItemName(bespokeOptionId));
			Configuration.wingPaintColour = bespokeOptionId;
		}
		else {

			// the same for the body.
			setBespokeBodyColour(rgba);
			setBespokeBodyPaintType(paintType);
			Configuration.bespokeBodyPaintColour = rgba;

			// take the wings with it if they were following it, exactly as picking a standard colour does - except that the colour
			// has to travel with the id, which for a bespoke option does not identify it.
			if (wereWingsFollowingBody) {
				setBespokeWingColour(rgba);
				setBespokeWingPaintType(paintType);
				Configuration.bespokeWingPaintColour = rgba;

				setSelectedWingColourItemId(bespokeOptionId);
				setSelectedWingColourItemName(getItemName(bespokeOptionId));
				Configuration.wingPaintColour = bespokeOptionId;
			}

			// record the option id against the body, and against the last body colour the wings are tested for following.
			setSelectedBodyColourItemId(bespokeOptionId);
			setSelectedBodyColourItemName(getItemName(bespokeOptionId));
			Configuration.bodyPaintColour = bespokeOptionId;
			Configuration.lastBodyPaintColour = bespokeOptionId;

			// rebuild the dashboard face and the splitter when the option id moves, so that both pick up the finish of this paint
			// type. these are the expensive calls, and they only need making when the finish can have changed - the colour they start
			// from is corrected on every move further down.
			if (hasOptionIdChanged) {
				props.interiorConfigurator.selectDashboardOption(Configuration.dashboardOption);
				props.exteriorConfigurator.selectSplitterBodyPaint(bespokeOptionId);
			}
		}

		// this handler fires on every pointer move while the picker is dragged, so the configuration is only re-priced when something
		// that carries a price actually moved: the option id, or - because one id serves both regions - whether they now differ, which
		// is what the two-tone option is charged for.
		if (hasOptionIdChanged || Configuration.isTwoTonePaint() !== wasTwoTonePaint) {
			Configuration.updateCosts();
		}

		// apply the colour to the car in real time, over the base material of that paint type.
		if (!isWingsPartSelected) {
			props.paintConfigurator.selectBespokeBodyPaint(rgba, paintType);
		}
		if (areWingsTakingColour) {
			props.paintConfigurator.selectBespokeWingPaint(rgba, paintType);
		}

		// keep the body-coloured parts with it. these are albedo writes only, so they are cheap enough to run on every movement of
		// the picker, and each does nothing at all when the part it owns is not following the body colour.
		if (!isWingsPartSelected) {
			props.interiorConfigurator.updateDashboardBodyColour(rgba);
			props.exteriorConfigurator.updateSplitterBodyColour(rgba);
		}

		// the front half of the splitter goes with the wings in the same way - rebuilt when their option id moves, so that it picks
		// up the finish of this paint type, and its colour corrected on every move.
		if (areWingsTakingColour) {
			if (hasWingOptionIdChanged) {
				props.exteriorConfigurator.selectSplitterWingPaint(bespokeOptionId);
			}
			props.exteriorConfigurator.updateSplitterWingColour(rgba);
		}
	};

	const handleItemClicked = (id: string) => {

		// apply the clicked colour to whichever part is currently being configured.
		if (isWingsPartSelected) {
			handleWingColourItemClicked(id);
		}
		else {
			handleBodyColourItemClicked(id);
		}
	};

	// return the HTML.
	return (
		<VehicleView
			onBackButtonClicked={props.onBackButtonClicked}
			onNextButtonClicked={props.onNextButtonClicked}>
			<div id="view-content">
				<div id="view-content-header">
					<h2 id="ga4-paintwork">
						PAINTWORK
					</h2>
				</div>

				<TabStrip
					tabs={paintPartTabs}
					selectedTabId={selectedPaintPart}
					onTabClicked={handlePaintPartClicked}
				/>

				<PaintView
					subHeading={'SOLID'}
					paintType={PaintTypes.Solid}
					paintPart={selectedPaintPart}
					itemId={selectedItemId}
					itemName={selectedItemName}
					onItemClicked={handleItemClicked}
					sectionPrice={selectedSectionPrice}
					modalContent={selectedModalContent}
					isClosed={openPaintType !== PaintTypes.Solid}
					onPanelOpenClose={(isOpen) => handlePanelOpenClose(PaintTypes.Solid, isOpen)}
					bespokeColour={selectedBespokeColour}
					isBespokeSelected={selectedBespokePaintType === PaintTypes.Solid}
					onBespokeColourChanged={handleBespokeColourChanged}
				/>
				<PaintView
					subHeading={'METALLIC'}
					paintType={PaintTypes.Metallic}
					paintPart={selectedPaintPart}
					itemId={selectedItemId}
					itemName={selectedItemName}
					onItemClicked={handleItemClicked}
					sectionPrice={selectedSectionPrice}
					modalContent={selectedModalContent}
					isClosed={openPaintType !== PaintTypes.Metallic}
					onPanelOpenClose={(isOpen) => handlePanelOpenClose(PaintTypes.Metallic, isOpen)}
					bespokeColour={selectedBespokeColour}
					isBespokeSelected={selectedBespokePaintType === PaintTypes.Metallic}
					onBespokeColourChanged={handleBespokeColourChanged}
				/>
				<PaintView
					subHeading={'SOLITALLIC'}
					paintType={PaintTypes.Solitallic}
					paintPart={selectedPaintPart}
					itemId={selectedItemId}
					itemName={selectedItemName}
					onItemClicked={handleItemClicked}
					sectionPrice={selectedSectionPrice}
					modalContent={selectedModalContent}
					isClosed={openPaintType !== PaintTypes.Solitallic}
					onPanelOpenClose={(isOpen) => handlePanelOpenClose(PaintTypes.Solitallic, isOpen)}
					bespokeColour={selectedBespokeColour}
					isBespokeSelected={selectedBespokePaintType === PaintTypes.Solitallic}
					onBespokeColourChanged={handleBespokeColourChanged}
				/>

			</div>
		</VehicleView>
	);
}

//-----------------------------------------------------------------------------------------
// Public Functions:
//-----------------------------------------------------------------------------------------

// returns the image to show for the currently selected body colour, given the path lookup for the finish it wants. a bespoke colour
// is arbitrary, so no swatch file exists for it - and the lookups below answer an unknown id with their fail-safe default, which would
// show a plausible but wrong colour here. a flat swatch of the real colour is generated instead.
export function getBodyColourImageFilePath(getFilePathForPaintColourId: (id: string) => string): string {

	// a bespoke colour is drawn rather than looked up.
	if (Configuration.isBespokePaintColour(Configuration.bodyPaintColour) && Configuration.bespokeBodyPaintColour) {
		return Utilities.colourSwatchDataUri(Utilities.rgbaToHex(Configuration.bespokeBodyPaintColour, true));
	}

	// otherwise use the selected paint colour's own swatch.
	return `assets/images/configuration-items/paintwork/${getFilePathForPaintColourId(Configuration.bodyPaintColour)}.webp`;
}

// used by views that have a 'matching body colour' option in order to display a swatch showing the currently selected body colour.
export function getFilePathForGlossPaintColourId(id: string): string {

	// map the paint style IDs to the file paths for the relevant swatches.
	const filePaths: Record<string, string> = {

		// solid.
		PS006: 'solid/gloss-solid-sport-black',
		PS004: 'solid/gloss-solid-sport-ivory',
		PS001: 'solid/gloss-solid-sport-green',
		PS002: 'solid/gloss-solid-sport-blue',
		PS003: 'solid/gloss-solid-sport-red',
		PS005: 'solid/gloss-solid-sport-dove-grey',
		PS014: 'solid/gloss-solid-classic-white',
		PS015: 'solid/gloss-solid-classic-dark-grey',
		PS013: 'solid/gloss-solid-classic-blue',
		PS012: 'solid/gloss-solid-classic-almond-green',
		PS017: 'solid/gloss-safari-yellow',
		PS018: 'solid/gloss-lagoon-blue',
		PS022: 'solid/gloss-belfast-blue',
		PS023: 'solid/gloss-vermiglia-red',
		PS024: 'solid/gloss-solid-classic-etna-blue',
		
		// metallic.
		PM001: 'metallic/metallic-black',
		PM003: 'metallic/dark-silver',
		PM008: 'metallic/metallic-graphite',
		PM005: 'metallic/ice-blue',
		PM002: 'metallic/metallic-silver',
		PM007: 'metallic/metallic-red',
		PM006: 'metallic/metallic-green',
		PM013: 'metallic/white-gold',
		PM014: 'metallic/alligator-green',
		PM015: 'metallic/regal-grey',
		PM019: 'metallic/edelweiss-green',
		PM020: 'metallic/furka-rouge',
		PM021: 'metallic/halcyon-gold',
		PM022: 'metallic/heather-green',
		PS025: 'metallic/classic-grassland-green',
		PM027: 'metallic/metallic-hill-green',
		PM028: 'metallic/metallic-acer-red',
		PM029: 'metallic/metallic-riviera-blue',

		// solitallic.
		PSM001: 'solitallic/solitallic-sport-green-gloss',
		PSM002: 'solitallic/solitallic-sport-blue-gloss',
		PSM003: 'solitallic/solitallic-classic-white-gloss',
		PM026: 'solitallic/solitallic-harbour-grey',
	};

	// return the corresponding path.
	const filePath = filePaths[id];
	if (filePath) {
		return filePath;
	}

	// or return a fail-save default if not found.
	return 'solid/gloss-solid-sport-black';
}

export function getFilePathForMattePaintColourId(id: string): string {
	
	// map the paint style IDs to the file paths for the relevant swatches.
	const filePaths: Record<string, string> = {

		// solid.
		PS006: 'solid/matte/matte-solid-sport-black',
		PS004: 'solid/matte/matte-solid-sport-ivory',
		PS001: 'solid/matte/matte-solid-sport-green',
		PS002: 'solid/matte/matte-solid-sport-blue',
		PS003: 'solid/matte/matte-solid-sport-red',
		PS005: 'solid/matte/matte-solid-sport-dove-grey',
		PS014: 'solid/matte/matte-solid-classic-white',
		PS015: 'solid/matte/matte-solid-classic-dark-grey',
		PS013: 'solid/matte/matte-solid-classic-blue',
		PS012: 'solid/matte/matte-solid-classic-almond-green',
		PS017: 'solid/matte/matte-safari-yellow',
		PS018: 'solid/matte/matte-lagoon-blue',
		PS022: 'solid/matte/matte-belfast-blue',
		PS023: 'solid/matte/matte-vermiglia-red',
		PS024: 'solid/matte/matte-solid-classic-etna-blue',
		
		// metallic.
		PM001: 'metallic/matte/matte-metallic-black',
		PM003: 'metallic/matte/matte-metallic-dark-silver',
		PM008: 'metallic/matte/matte-metallic-graphite',
		PM005: 'metallic/matte/matte-metallic-ice-blue',
		PM002: 'metallic/matte/matte-metallic-silver',
		PM007: 'metallic/matte/matte-metallic-red',
		PM006: 'metallic/matte/matte-metallic-green',
		PM013: 'metallic/matte/matte-white-gold',
		PM014: 'metallic/matte/matte-alligator-green',
		PM015: 'metallic/matte/matte-regal-grey',
		PM019: 'metallic/matte/matte-edelweiss-green',
		PM020: 'metallic/matte/matte-furka-rouge',
		PM021: 'metallic/matte/matte-halcyon-gold',
		PM022: 'metallic/matte/matte-heather-green',
		PS025: 'metallic/matte/matte-classic-grassland-green',
		PM027: 'metallic/matte/matte-metallic-hill-green',
		PM028: 'metallic/matte/matte-metallic-acer-red',
		PM029: 'metallic/matte/matte-metallic-riviera-blue',

		// solitalic.
		PSM001: 'solitallic/matte/solitallic-sport-green-matte',
		PSM002: 'solitallic/matte/solitallic-sport-blue-matte',
		PSM003: 'solitallic/matte/solitallic-classic-white-matte',
		PM026: 'solitallic/matte/solitallic-harbour-grey',
	};

	// return the corresponding path.
	const filePath = filePaths[id];
	if (filePath) {
		return filePath;
	}

	// or return a fail-save default if not found.
	return 'solid/matte/matte-solid-sport-black';
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow the paint item fn to access the selected item id from the paint view and call its item click handler.
const PaintViewContext = createContext<PaintViewContextType | undefined>(undefined);

function PaintView(props: PaintViewProps) {

	// set the selected item ID from the given prop.
	const selectedItemId = props.itemId;

	// the bespoke option is only shown on the paint types that offer it, on either tab - the body and the wings can each be given a
	// colour of their own.
	const hasBespokeColourOption = BESPOKE_PAINT_OPTION_IDS[props.paintType] !== undefined;

	// if an item is clicked, call the given handler.
	const handleItemClicked = (id: string) => {
		props.onItemClicked(id);
	};

	// return the HTML.
	return (
		<PaintViewContext.Provider value={{ selectedItemId, handleItemClicked }}>
			<SectionPanel
				subHeading={props.subHeading}
				modalContent={props.modalContent}
				isClosed={props.isClosed}
				onPanelOpenClose={props.onPanelOpenClose}
			>
				<SelectedSectionItem
					itemName={props.itemName}
					sectionPrice={props.sectionPrice}
				/>
				{hasBespokeColourOption &&
					/* keyed on the region so that changing tab re-mounts the option, closing its picker. the picker seeds its hue,
					   saturation and value from 'initialColour' on mount and never again, so one left open across a tab change would
					   go on showing the previous region's colour while the swatch above it showed the new one. */
					<BespokePaintOption
						key={props.paintPart}
						colour={props.bespokeColour}
						isSelected={props.isBespokeSelected}
						isPanelClosed={props.isClosed}
						onColourChanged={(rgba) => props.onBespokeColourChanged(rgba, props.paintType)}
					/>
				}
				<PaintItems paintType={props.paintType} />
			</SectionPanel>
		</PaintViewContext.Provider>
	);
}

// the 'select your own' option shown above the fixed colours in a paint panel: a swatch of the bespoke colour that expands into the colour picker.
// n.b. this is the natural home for future bespoke-specific functionality (a price, an info modal, seeding the colour from the Configuration).
function BespokePaintOption(props: BespokePaintOptionProps) {

	// the colour has not been chosen until the customer has been in the picker, so until then the swatch shows the 'special colour'
	// icon instead of a fill, and the picker - and the colour applied when it is opened - falls back to its own default. this is the
	// one place that fallback is made, so the default itself stays owned by the portable picker.
	const hasChosenColour = props.colour !== null;
	const colour = props.colour ?? DEFAULT_BESPOKE_COLOUR;

	// states.
	const [isPickerOpen, setIsPickerOpen] = useState(false);

	// the picker seeds itself from 'initialColour' when it mounts and not afterwards, so it is re-mounted on each opening - keyed on
	// the number of times it has been opened - to start it on the shared colour as it stands now rather than the one it first saw.
	const [openCount, setOpenCount] = useState(0);

	// whether this option is showing the special paint information, which happens once only, on the customer's first selection of a
	// bespoke colour in any panel. once shown the modal is left mounted, as closing it only hides it.
	const [isShowingSpecialPaintInformation, setIsShowingSpecialPaintInformation] = useState(false);

	// close the picker whenever the panel it sits in closes, so that it is not still open when that panel is next opened.
	// this adjusts state during rendering rather than in an effect, which is react's pattern for resetting state from a prop change.
	const [wasPanelClosed, setWasPanelClosed] = useState(props.isPanelClosed);
	if (props.isPanelClosed !== wasPanelClosed) {
		setWasPanelClosed(props.isPanelClosed);
		if (props.isPanelClosed) {
			setIsPickerOpen(false);
		}
	}

	// handlers.

	const handleSwatchClicked = () => {

		// opening the picker re-applies the shared colour with this panel's paint type, which is how the same colour is compared
		// across finishes. closing it is left neutral, as a click to collapse the picker and go back to the fixed colours should
		// not re-apply anything.
		if (!isPickerOpen) {
			setOpenCount(openCount + 1);
			props.onColourChanged(colour);

			// the first selection of a bespoke colour also shows the special paint information. the flag lives in the configuration,
			// as this view is unmounted on every change of step, and is shared by every panel's option - and by both tabs - so it is
			// shown once in all.
			if (!Configuration.hasShownSpecialPaintInformation) {
				Configuration.hasShownSpecialPaintInformation = true;
				setIsShowingSpecialPaintInformation(true);
			}
		}

		// toggle the picker.
		setIsPickerOpen(!isPickerOpen);
	};

	// return the HTML.
	return (
		<div className="bespoke-paint-option">

			<div className="bespoke-paint-option-header">
				<a
					role="button"
					className={`bespoke-paint-option-swatch${hasChosenColour ? '' : ' default'}${props.isSelected ? ' selected' : ''}${isPickerOpen ? ' open' : ''}`}
					style={hasChosenColour ? { backgroundColor: rgbToHex(colour) } : undefined}
					title="Choose your own colour"
					onClick={handleSwatchClicked}
				>
					{/* before a colour is chosen the swatch is the icon rather than a fill - an image, as the fixed colours are, so it sizes with them. */}
					{!hasChosenColour &&
						<img draggable={false} className="bespoke-paint-option-swatch-icon" src={SPECIAL_COLOUR_IMAGE_FILE_PATH} alt="Special colour" />
					}
				</a>
				{/* the label doubles as the selected state, reading 'SELECTED' once this panel's bespoke colour is the one on the car. */}
				<p className="bespoke-paint-option-label">{props.isSelected ? 'SELECTED' : 'SELECT YOUR OWN'}</p>
			</div>

			<div className={`bespoke-paint-option-picker${isPickerOpen ? ' open' : ''}`}>
				<BespokeColourPicker key={openCount} initialColour={colour} onColourChanged={props.onColourChanged} />
			</div>

			{/* a modal given no trigger opens as it mounts. it sits outside the picker, whose scale transform would otherwise become */}
			{/* the containing block of its fixed position and shrink it into the picker's box. */}
			{isShowingSpecialPaintInformation &&
				<Modal content={SPECIAL_PAINT_MODAL_CONTENT} />
			}

		</div>
	);
}

function PaintItems(props: PaintItemsProps) {
	switch (props.paintType) {
		case PaintTypes.Metallic:
			return <PaintItemsMetallic />;
		case PaintTypes.Solitallic:
			return <PaintItemsSolitallic />;
		default:
			return <PaintItemsSolid />;
	}
};

function PaintItemsSolid() {
	return (
		<ViewContentRows maxRowLength={8}>
			<PaintItem id={'PS006'} imageFilePath={'solid/gloss-solid-sport-black'} />
			<PaintItem id={'PS004'} imageFilePath={'solid/gloss-solid-sport-ivory'} />
			<PaintItem id={'PS001'} imageFilePath={'solid/gloss-solid-sport-green'} />
			<PaintItem id={'PS002'} imageFilePath={'solid/gloss-solid-sport-blue'} />
			<PaintItem id={'PS003'} imageFilePath={'solid/gloss-solid-sport-red'} />
			<PaintItem id={'PS005'} imageFilePath={'solid/gloss-solid-sport-dove-grey'} />
			<PaintItem id={'PS014'} imageFilePath={'solid/gloss-solid-classic-white'} />
			<PaintItem id={'PS015'} imageFilePath={'solid/gloss-solid-classic-dark-grey'} />
			<PaintItem id={'PS013'} imageFilePath={'solid/gloss-solid-classic-blue'} />
			<PaintItem id={'PS012'} imageFilePath={'solid/gloss-solid-classic-almond-green'} />
			<PaintItem id={'PS017'} imageFilePath={'solid/gloss-safari-yellow'} />
			<PaintItem id={'PS018'} imageFilePath={'solid/gloss-lagoon-blue'} />
			<PaintItem id={'PS022'} imageFilePath={'solid/gloss-belfast-blue'} />
			<PaintItem id={'PS023'} imageFilePath={'solid/gloss-vermiglia-red'} />
			<PaintItem id={'PS024'} imageFilePath={'solid/gloss-solid-classic-etna-blue'} />
		</ViewContentRows>
	);
}

function PaintItemsMetallic() {
	return (
		<ViewContentRows maxRowLength={7}>
			<PaintItem id={'PM001'} imageFilePath={'metallic/metallic-black'} />
			<PaintItem id={'PM003'} imageFilePath={'metallic/dark-silver'} />
			<PaintItem id={'PM008'} imageFilePath={'metallic/metallic-graphite'} />
			<PaintItem id={'PM005'} imageFilePath={'metallic/ice-blue'} />
			<PaintItem id={'PM002'} imageFilePath={'metallic/metallic-silver'} />
			<PaintItem id={'PM007'} imageFilePath={'metallic/metallic-red'} />
			<PaintItem id={'PM006'} imageFilePath={'metallic/metallic-green'} />
			<PaintItem id={'PM013'} imageFilePath={'metallic/white-gold'} />
			<PaintItem id={'PM014'} imageFilePath={'metallic/alligator-green'} />
			<PaintItem id={'PM015'} imageFilePath={'metallic/regal-grey'} />
			<PaintItem id={'PM019'} imageFilePath={'metallic/edelweiss-green'} />
			<PaintItem id={'PM020'} imageFilePath={'metallic/furka-rouge'} />
			<PaintItem id={'PM021'} imageFilePath={'metallic/halcyon-gold'} />
			<PaintItem id={'PM022'} imageFilePath={'metallic/heather-green'} />
			<PaintItem id={'PS025'} imageFilePath={'metallic/classic-grassland-green'} />
			<PaintItem id={'PM027'} imageFilePath={'metallic/metallic-hill-green'} />
			<PaintItem id={'PM028'} imageFilePath={'metallic/metallic-acer-red'} />
			<PaintItem id={'PM029'} imageFilePath={'metallic/metallic-riviera-blue'} />
		</ViewContentRows>
	);
}

function PaintItemsSolitallic() {
	return (
		<ViewContentRows>
			<PaintItem id={'PSM001'} imageFilePath={'solitallic/solitallic-sport-green-gloss'} />
			<PaintItem id={'PSM002'} imageFilePath={'solitallic/solitallic-sport-blue-gloss'} />
			<PaintItem id={'PSM003'} imageFilePath={'solitallic/solitallic-classic-white-gloss'} />
			<PaintItem id={'PM026'} imageFilePath={'solitallic/solitallic-harbour-grey'} />
		</ViewContentRows>
	);
}

function PaintItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(PaintViewContext);
	if (!context) {
		throw new Error('PaintViewContext must be used within a PaintView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/paintwork/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedItemId}
			onItemClicked={context.handleItemClicked}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.paintworkColourOptions);
}

// returns the content for the info modal of the given paint id - the special paint information for a bespoke colour, whose option
// ids have no back-office description to show, or the option's own description for every standard colour.
function getModalContent(id: string): ModalContent[] {
	if (Configuration.isBespokePaintColour(id)) {
		return SPECIAL_PAINT_MODAL_CONTENT;
	}
	return CloudData.getModalContent(id, CloudData.paintworkColourOptions);
}

// builds the image for the given paint selection's swatch, which is what lets each tab show the colour its part is currently painted.
// takes the region's own bespoke colour rather than reading one, as the body and the wings each have one and record the same ids.
// a bespoke colour has no swatch file, and the lookup below would answer its id with the fail-safe default - a plausible but wrong
// colour - so a flat swatch of the real colour is generated instead.
function getSwatchFilePathForColour(id: string, bespokeColour: NumberArray | null): string {

	// a bespoke colour is drawn rather than looked up.
	if (Configuration.isBespokePaintColour(id) && bespokeColour) {
		return Utilities.colourSwatchDataUri(Utilities.rgbaToHex(bespokeColour, true));
	}

	// otherwise use the colour's own swatch.
	return `assets/images/configuration-items/paintwork/${getFilePathForGlossPaintColourId(id)}.webp`;
}

// returns the paint type a bespoke option id belongs to, or null when the given id is a standard colour - which is the state that
// has no bespoke panel showing as selected.
function getBespokePaintTypeForId(id: string): PaintTypes | null {
	return Configuration.isBespokePaintColour(id) ? CloudData.getPaintTypeForId(id) : null;
}
